// Shared helper for mapping lease-creation "Property Condition Photos"
// (free-text captions like "Kitchen", "Bed Room", "Home Theater") to
// the fixed vacate category enum. Used by:
//
//   - VacateDetailPage (landlord side, "Photo Comparison" rows)
//   - VacatePage       (tenant side, "Move-In reference" strip on each
//                       category card so the tenant has a visual anchor
//                       while taking move-out photos)
//
// Strategy:
//   1. Normalize the caption (lowercase, strip non-alphanumeric).
//   2. Walk the synonym table — first hit wins.
//   3. If a synonym matches as substring, use that category.
//   4. Otherwise → 'other' bucket.
//
// Add new entries when real-world captions surface in production.

const _CAPTION_SYNONYMS = [
  // hall / living room
  ['hall', ['hall', 'living', 'livingroom', 'drawing', 'drawingroom', 'sittingroom', 'lounge', 'parlor', 'parlour']],
  // bedroom
  ['bedroom', ['bedroom', 'bed', 'masterbedroom', 'guestroom', 'guestbedroom']],
  // kitchen
  ['kitchen', ['kitchen', 'pantry', 'cookingarea']],
  // bathroom
  ['bathroom', ['bathroom', 'bath', 'toilet', 'washroom', 'restroom', 'wc']],
  // walls (covers paint / damage / general wall photos)
  ['walls', ['wall', 'walls', 'paint', 'painting']],
  // meter readings (electricity / water / gas)
  ['meter', ['meter', 'reading', 'electricity', 'eb', 'tneb', 'gas', 'watermeter']],
];

export function captionToCategory(caption) {
  const c = String(caption || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!c) return 'other';
  for (const [cat, syns] of _CAPTION_SYNONYMS) {
    for (const syn of syns) {
      if (c.includes(syn)) return cat;
    }
  }
  return 'other';
}

// Categories the comparison UI renders. Kept in sync with the server's
// REQUIRED_PHOTO_CATEGORIES + OPTIONAL (server/routes/vacate.js).
const KNOWN_CATEGORIES = new Set(['hall', 'bedroom', 'kitchen', 'bathroom', 'walls', 'meter', 'other']);

/**
 * Group lease.terms.condition_photos by resolved category.
 * @param {Array} photos    Array of { url, caption, notes, addedBy, ... }
 * @returns {object}        { hall: [...], bedroom: [...], kitchen: [...], bathroom: [...], walls: [...], meter: [...], other: [...] }
 */
export function groupMoveInByCategory(photos) {
  const out = {};
  if (!Array.isArray(photos)) return out;
  for (const p of photos) {
    // Trust the STRUCTURED category first. Newer photos carry an explicit
    // `category` chosen by the uploader; only legacy rows (captured before
    // that field existed) need the caption-synonym fallback.
    //
    // This used to key off the caption alone, which silently misfiled
    // evidence at the worst possible moment: a photo saved under Kitchen
    // but captioned "Stove area" resolved to `other`, so it never lined up
    // against its move-out counterpart in the settlement comparison.
    const explicit = typeof p?.category === 'string' ? p.category.trim().toLowerCase() : '';
    const cat = KNOWN_CATEGORIES.has(explicit) ? explicit : captionToCategory(p?.caption);
    (out[cat] = out[cat] || []).push(p);
  }
  return out;
}

/**
 * Pull move-in photos out of a lease object — handles both string-JSON
 * and parsed-object shapes of lease.terms (the DB column is TEXT, server
 * sync returns parsed in some paths and raw string in others).
 */
export function leaseMoveInPhotos(lease) {
  if (!lease) return [];
  try {
    const terms = typeof lease.terms === 'string' ? JSON.parse(lease.terms) : (lease.terms || {});
    return Array.isArray(terms.condition_photos) ? terms.condition_photos : [];
  } catch {
    return [];
  }
}
